import React, { useEffect, useState, useRef, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Send, CheckCircle, X, Search, Plus, Filter, Trash2 } from 'lucide-react';

const th = { padding:'10px 14px', textAlign:'left', fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035' };
const td = { padding:'12px 14px', fontSize:13, color:'#cbd5e1', borderBottom:'1px solid #0f1117', verticalAlign:'top' };

const inp = { width:'100%', padding:'10px 12px', borderRadius:8, background:'#0f1117', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', marginBottom:12, boxSizing:'border-box' };

export default function NotaVendedor() {
  const { user, isPreVenda, isVendedor, isChefe, isGerenteComercial } = useAuth();
  const podeEditar = isChefe() || isGerenteComercial() || isPreVenda();
  const podeResponder = isChefe() || isGerenteComercial() || isVendedor();
  const podeCriar = isChefe() || isGerenteComercial() || isPreVenda();

  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [toast, setToast] = useState('');

  // Filtros
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroVendedor, setFiltroVendedor] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');

  // Edição de texto de nota
  const [editandoId, setEditandoId] = useState(null);
  const [editTexto, setEditTexto] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Resposta
  const [respondendoId, setRespondendoId] = useState(null);
  const [respostaTexto, setRespostaTexto] = useState('');
  const [salvandoResp, setSalvandoResp] = useState(false);

  // Exclusão
  const [deletandoId, setDeletandoId] = useState(null);

  // Modal Criar Nota
  const [showCriarModal, setShowCriarModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  const [criandoLeadId, setCriandoLeadId] = useState(null);
  const [novoTexto, setNovoTexto] = useState('');
  const [criando, setCriando] = useState(false);
  const [criandoErro, setCriandoErro] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = () => {
    setLoading(true); setErro('');
    api.get('/notas-vendedor/')
      .then(r => setNotas(r.data))
      .catch(() => setErro('Erro ao carregar dados. Tente atualizar a página.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const abrirCriar = () => {
    setShowCriarModal(true);
    setCriandoLeadId(null); setNovoTexto(''); setLeadSearch(''); setCriandoErro('');
    setLeadsLoading(true);
    api.get('/notas-vendedor/leads/')
      .then(r => setLeads(r.data))
      .catch(() => setLeads([]))
      .finally(() => setLeadsLoading(false));
  };

  const criarNota = async () => {
    if (!criandoLeadId) { setCriandoErro('Selecione um cliente.'); return; }
    if (!novoTexto.trim()) { setCriandoErro('Escreva a nota.'); return; }
    setCriando(true); setCriandoErro('');
    try {
      await api.post('/notas-vendedor/', { lead_id: criandoLeadId, texto: novoTexto });
      showToast('Nota criada com sucesso!');
      setShowCriarModal(false);
      load();
    } catch (e) {
      setCriandoErro('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setCriando(false); }
  };

  const salvarNota = async (id) => {
    if (!editTexto.trim()) return;
    setSalvando(true);
    try {
      await api.patch(`/notas-vendedor/${id}/`, { texto: editTexto });
      showToast('Nota salva!');
      setEditandoId(null);
      load();
    } catch (e) {
      showToast('Erro ao salvar nota.');
    } finally { setSalvando(false); }
  };

  const excluirNota = async (id) => {
    setDeletandoId(id);
    try {
      await api.delete(`/notas-vendedor/${id}/`);
      showToast('Nota excluída!');
      load();
    } catch (e) {
      showToast('Erro ao excluir nota.');
    } finally { setDeletandoId(null); }
  };

  const salvarResposta = async (id) => {
    if (!respostaTexto.trim()) return;
    setSalvandoResp(true);
    try {
      await api.patch(`/notas-vendedor/${id}/responder/`, { resposta: respostaTexto });
      showToast('Resposta salva!');
      setRespondendoId(null);
      load();
    } catch (e) {
      showToast('Erro ao salvar resposta.');
    } finally { setSalvandoResp(false); }
  };

  const filteredLeads = leads.filter(l =>
    l.cliente_nome.toLowerCase().includes(leadSearch.toLowerCase()) ||
    (l.vendedor_nome || '').toLowerCase().includes(leadSearch.toLowerCase())
  );

  // Lista única de vendedores para o select do filtro
  const vendedoresDisponiveis = useMemo(() => {
    const map = {};
    notas.forEach(n => {
      if (n.lead_vendedor_nome && !map[n.lead_vendedor_nome]) {
        map[n.lead_vendedor_nome] = true;
      }
    });
    return Object.keys(map).sort();
  }, [notas]);

  // Filtragem client-side
  const notasFiltradas = useMemo(() => {
    const parseCriadoEm = (str) => {
      // "dd/mm/yyyy às HH:MM"
      try {
        const datePart = str.split(' às ')[0];
        const [d, m, y] = datePart.split('/');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      } catch { return null; }
    };
    return notas.filter(n => {
      if (filtroCliente && !n.lead_cliente.toLowerCase().includes(filtroCliente.toLowerCase())) return false;
      if (filtroVendedor && (n.lead_vendedor_nome || '') !== filtroVendedor) return false;
      if (filtroDataInicio || filtroDataFim) {
        const dt = parseCriadoEm(n.criado_em);
        if (!dt) return false;
        if (filtroDataInicio) {
          const ini = new Date(filtroDataInicio + 'T00:00:00');
          if (dt < ini) return false;
        }
        if (filtroDataFim) {
          const fim = new Date(filtroDataFim + 'T23:59:59');
          if (dt > fim) return false;
        }
      }
      return true;
    });
  }, [notas, filtroCliente, filtroVendedor, filtroDataInicio, filtroDataFim]);

  const temFiltro = filtroCliente || filtroVendedor || filtroDataInicio || filtroDataFim;
  const limparFiltros = () => { setFiltroCliente(''); setFiltroVendedor(''); setFiltroDataInicio(''); setFiltroDataFim(''); };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <div style={{ fontSize:22, fontWeight:700, color:'#f6c90e' }}>Nota Vendedor</div>
        {podeCriar && (
          <button
            onClick={abrirCriar}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, background:'rgba(246,201,14,0.12)', border:'1px solid rgba(246,201,14,0.3)', color:'#f6c90e', fontSize:13, fontWeight:700, cursor:'pointer' }}
          >
            <Plus size={14} /> Criar
          </button>
        )}
      </div>
      <div style={{ fontSize:13, color:'#475569', marginBottom:16 }}>
        Notas enviadas pelo Pré Venda ao responsável pelo lead
      </div>

      {/* ── Barra de filtros ── */}
      <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <Filter size={14} color="#475569" style={{ flexShrink:0 }} />
          {/* Filtro cliente */}
          <div style={{ display:'flex', alignItems:'center', gap:6, background:'#0f1117', border:'1px solid #1a2035', borderRadius:7, padding:'6px 10px', flex:'1 1 160px', minWidth:140 }}>
            <Search size={12} color="#475569" />
            <input
              style={{ flex:1, background:'none', border:'none', color:'#e2e8f0', fontSize:12, outline:'none' }}
              placeholder="Filtrar por cliente..."
              value={filtroCliente}
              onChange={e => setFiltroCliente(e.target.value)}
            />
          </div>
          {/* Filtro vendedor */}
          <select
            style={{ flex:'1 1 160px', minWidth:140, padding:'6px 10px', borderRadius:7, background:'#0f1117', border:'1px solid #1a2035', color: filtroVendedor ? '#e2e8f0' : '#64748b', fontSize:12, outline:'none', cursor:'pointer' }}
            value={filtroVendedor}
            onChange={e => setFiltroVendedor(e.target.value)}
          >
            <option value="">Todos os vendedores</option>
            {vendedoresDisponiveis.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          {/* Filtro data início */}
          <div style={{ display:'flex', alignItems:'center', gap:6, flex:'1 1 140px', minWidth:130 }}>
            <span style={{ fontSize:11, color:'#475569', whiteSpace:'nowrap' }}>De:</span>
            <input
              type="date"
              style={{ flex:1, padding:'5px 8px', borderRadius:7, background:'#0f1117', border:'1px solid #1a2035', color: filtroDataInicio ? '#e2e8f0' : '#475569', fontSize:12, outline:'none' }}
              value={filtroDataInicio}
              onChange={e => setFiltroDataInicio(e.target.value)}
            />
          </div>
          {/* Filtro data fim */}
          <div style={{ display:'flex', alignItems:'center', gap:6, flex:'1 1 140px', minWidth:130 }}>
            <span style={{ fontSize:11, color:'#475569', whiteSpace:'nowrap' }}>Até:</span>
            <input
              type="date"
              style={{ flex:1, padding:'5px 8px', borderRadius:7, background:'#0f1117', border:'1px solid #1a2035', color: filtroDataFim ? '#e2e8f0' : '#475569', fontSize:12, outline:'none' }}
              value={filtroDataFim}
              onChange={e => setFiltroDataFim(e.target.value)}
            />
          </div>
          {/* Limpar filtros */}
          {temFiltro && (
            <button
              onClick={limparFiltros}
              style={{ padding:'5px 12px', borderRadius:7, background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.25)', color:'#f87171', fontSize:12, cursor:'pointer', whiteSpace:'nowrap' }}
            >
              <X size={11} style={{ verticalAlign:'middle', marginRight:4 }} />Limpar
            </button>
          )}
        </div>
        {temFiltro && (
          <div style={{ fontSize:11, color:'#475569', marginTop:8 }}>
            {notasFiltradas.length} nota{notasFiltradas.length !== 1 ? 's' : ''} encontrada{notasFiltradas.length !== 1 ? 's' : ''} de {notas.length} no total
          </div>
        )}
      </div>

      {toast && (
        <div style={{ position:'fixed', top:20, right:20, background:'rgba(74,222,128,0.15)', border:'1px solid rgba(74,222,128,0.4)', borderRadius:10, padding:'12px 20px', color:'#4ade80', fontSize:13, fontWeight:600, zIndex:2000, display:'flex', alignItems:'center', gap:8 }}>
          <CheckCircle size={16} /> {toast}
        </div>
      )}

      {erro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{erro}</div>
      )}

      {loading ? (
        <div style={{ color:'#475569', fontSize:13 }}>Carregando...</div>
      ) : notas.length === 0 ? (
        <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:32, textAlign:'center', color:'#475569', fontSize:13 }}>
          Nenhuma nota registrada ainda.
        </div>
      ) : notasFiltradas.length === 0 ? (
        <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:32, textAlign:'center', color:'#475569', fontSize:13 }}>
          Nenhuma nota encontrada com os filtros aplicados.
        </div>
      ) : (
        <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflowX:'auto', overflowY:'auto', maxHeight:500 }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Cliente</th>
                <th style={th}>Responsável</th>
                <th style={th}>Nota</th>
                <th style={th}>Resposta</th>
                <th style={th}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {notasFiltradas.map(n => (
                <React.Fragment key={n.id}>
                  <tr
                    onMouseEnter={e => e.currentTarget.style.background='#111827'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={{ ...td, fontWeight:600, color:'#e2e8f0', whiteSpace:'nowrap' }}>{n.lead_cliente}</td>
                    <td style={td}>
                      {n.lead_vendedor_nome
                        ? <span style={{ color:'#f6c90e', fontWeight:600 }}>{n.lead_vendedor_nome}</span>
                        : <span style={{ color:'#334155' }}>—</span>}
                    </td>
                    <td style={{ ...td, maxWidth:280 }}>
                      <div style={{ fontSize:13, color:'#e2e8f0', lineHeight:1.5, marginBottom:4 }}>{n.texto}</div>
                      <div style={{ fontSize:11, color:'#475569' }}>
                        Nota criada por {n.criado_por_nome} dia {n.criado_em}
                      </div>
                    </td>
                    <td style={{ ...td, maxWidth:280 }}>
                      {n.resposta ? (
                        <>
                          <div style={{ fontSize:13, color:'#e2e8f0', lineHeight:1.5, marginBottom:4 }}>{n.resposta}</div>
                          <div style={{ fontSize:11, color:'#475569' }}>
                            Resposta enviada por {n.respondido_por_nome} dia {n.respondido_em}
                          </div>
                        </>
                      ) : (
                        <span style={{ color:'#334155', fontSize:12 }}>Sem resposta</span>
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace:'nowrap' }}>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        {podeEditar && (
                          <button
                            onClick={() => { setEditandoId(editandoId === n.id ? null : n.id); setEditTexto(n.texto); setRespondendoId(null); }}
                            style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:7, background: editandoId === n.id ? 'rgba(248,113,113,0.1)' : 'rgba(246,201,14,0.1)', color: editandoId === n.id ? '#f87171' : '#f6c90e', border:`1px solid ${editandoId === n.id ? 'rgba(248,113,113,0.3)' : 'rgba(246,201,14,0.3)'}`, fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                            {editandoId === n.id ? 'Cancelar' : <><Send size={11} /> Editar Nota</>}
                          </button>
                        )}
                        {podeResponder && (
                          <button
                            onClick={() => { setRespondendoId(respondendoId === n.id ? null : n.id); setRespostaTexto(n.resposta || ''); setEditandoId(null); }}
                            style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:7, background: respondendoId === n.id ? 'rgba(248,113,113,0.1)' : 'rgba(96,165,250,0.1)', color: respondendoId === n.id ? '#f87171' : '#60a5fa', border:`1px solid ${respondendoId === n.id ? 'rgba(248,113,113,0.3)' : 'rgba(96,165,250,0.3)'}`, fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                            {respondendoId === n.id ? 'Cancelar' : <><Send size={11} /> {n.resposta ? 'Editar Resposta' : 'Responder'}</>}
                          </button>
                        )}
                        {podeEditar && (
                          <button
                            onClick={() => { if (window.confirm('Excluir esta nota?')) excluirNota(n.id); }}
                            disabled={deletandoId === n.id}
                            style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:7, background:'rgba(248,113,113,0.08)', color:'#f87171', border:'1px solid rgba(248,113,113,0.25)', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', opacity: deletandoId === n.id ? 0.5 : 1 }}>
                            <Trash2 size={11} /> {deletandoId === n.id ? 'Excluindo...' : 'Excluir'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Linha edição nota */}
                  {editandoId === n.id && (
                    <tr>
                      <td colSpan={5} style={{ padding:'14px 18px', background:'rgba(246,201,14,0.04)', borderBottom:'1px solid #1a2035' }}>
                        <div style={{ fontSize:12, color:'#94a3b8', marginBottom:8, fontWeight:600 }}>✏️ Editar nota — {n.lead_cliente}</div>
                        <textarea
                          value={editTexto}
                          onChange={e => setEditTexto(e.target.value)}
                          rows={3}
                          style={{ width:'100%', background:'#0e1420', border:'1px solid #1e2d45', borderRadius:8, padding:'10px 14px', color:'#e2e8f0', fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box' }}
                        />
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
                          <button onClick={() => setEditandoId(null)} style={{ padding:'7px 16px', borderRadius:7, background:'transparent', color:'#64748b', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}>Cancelar</button>
                          <button onClick={() => salvarNota(n.id)} disabled={salvando || !editTexto.trim()} style={{ padding:'7px 18px', borderRadius:7, background: editTexto.trim() ? 'rgba(246,201,14,0.15)' : '#1a2035', color: editTexto.trim() ? '#f6c90e' : '#475569', border:`1px solid ${editTexto.trim() ? 'rgba(246,201,14,0.4)' : '#1e2d45'}`, fontSize:13, fontWeight:700, cursor: editTexto.trim() ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', gap:6 }}>
                            <Send size={13} /> {salvando ? 'Salvando...' : 'Salvar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Linha resposta */}
                  {respondendoId === n.id && (
                    <tr>
                      <td colSpan={5} style={{ padding:'14px 18px', background:'rgba(96,165,250,0.04)', borderBottom:'1px solid #1a2035' }}>
                        <div style={{ fontSize:12, color:'#94a3b8', marginBottom:8, fontWeight:600 }}>💬 Resposta — {n.lead_cliente}</div>
                        <textarea
                          value={respostaTexto}
                          onChange={e => setRespostaTexto(e.target.value)}
                          rows={3}
                          placeholder="Digite a resposta..."
                          style={{ width:'100%', background:'#0e1420', border:'1px solid #1e2d45', borderRadius:8, padding:'10px 14px', color:'#e2e8f0', fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box' }}
                        />
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
                          <button onClick={() => setRespondendoId(null)} style={{ padding:'7px 16px', borderRadius:7, background:'transparent', color:'#64748b', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}>Cancelar</button>
                          <button onClick={() => salvarResposta(n.id)} disabled={salvandoResp || !respostaTexto.trim()} style={{ padding:'7px 18px', borderRadius:7, background: respostaTexto.trim() ? 'rgba(96,165,250,0.15)' : '#1a2035', color: respostaTexto.trim() ? '#60a5fa' : '#475569', border:`1px solid ${respostaTexto.trim() ? 'rgba(96,165,250,0.4)' : '#1e2d45'}`, fontSize:13, fontWeight:700, cursor: respostaTexto.trim() ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', gap:6 }}>
                            <Send size={13} /> {salvandoResp ? 'Salvando...' : 'Salvar Resposta'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal Criar Nota ──────────────────────────────────────────────────── */}
      {showCriarModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
          onClick={e => { if (e.target === e.currentTarget) setShowCriarModal(false); }}>
          <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:14, padding:28, width:520, maxWidth:'95vw', maxHeight:'90vh', display:'flex', flexDirection:'column' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
              <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e' }}>Criar Nota para Vendedor</div>
              <button onClick={() => setShowCriarModal(false)} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer' }}><X size={18} /></button>
            </div>

            {/* Busca de cliente */}
            <div style={{ display:'flex', alignItems:'center', gap:8, background:'#0f1117', border:'1px solid #1a2035', borderRadius:8, padding:'8px 12px', marginBottom:12 }}>
              <Search size={14} color="#475569" />
              <input
                style={{ flex:1, background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none' }}
                placeholder="Buscar cliente..."
                value={leadSearch}
                onChange={e => { setLeadSearch(e.target.value); setCriandoLeadId(null); setNovoTexto(''); }}
              />
            </div>

            <div style={{ flex:1, overflowY:'auto', maxHeight:320, marginBottom:12 }}>
              {leadsLoading ? (
                <div style={{ color:'#475569', fontSize:13, padding:16, textAlign:'center' }}>Carregando...</div>
              ) : filteredLeads.length === 0 ? (
                <div style={{ color:'#475569', fontSize:13, padding:16, textAlign:'center' }}>Nenhum cliente encontrado.</div>
              ) : filteredLeads.map(l => (
                <div key={l.lead_id}>
                  <div
                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', borderRadius:8, marginBottom:4, background: criandoLeadId === l.lead_id ? 'rgba(246,201,14,0.06)' : '#080c14', border: criandoLeadId === l.lead_id ? '1px solid rgba(246,201,14,0.3)' : '1px solid #1a2035', cursor:'pointer' }}
                    onClick={() => { setCriandoLeadId(l.lead_id); setNovoTexto(''); setCriandoErro(''); }}
                  >
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:'#e2e8f0' }}>{l.cliente_nome}</div>
                      {l.vendedor_nome && <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>Responsável: {l.vendedor_nome}</div>}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCriandoLeadId(l.lead_id); setCriandoErro(''); }}
                      style={{ background:'rgba(246,201,14,0.1)', border:'1px solid rgba(246,201,14,0.3)', borderRadius:6, padding:'4px 10px', color:'#f6c90e', cursor:'pointer', fontSize:18, lineHeight:1 }}
                    >+</button>
                  </div>

                  {criandoLeadId === l.lead_id && (
                    <div style={{ background:'rgba(246,201,14,0.04)', border:'1px solid rgba(246,201,14,0.15)', borderRadius:8, padding:'12px 14px', marginBottom:8, marginTop:-2 }}>
                      <textarea
                        value={novoTexto}
                        onChange={e => setNovoTexto(e.target.value)}
                        rows={3}
                        placeholder="Escreva a nota para o vendedor..."
                        autoFocus
                        style={{ width:'100%', background:'#0e1420', border:'1px solid #1e2d45', borderRadius:8, padding:'10px 14px', color:'#e2e8f0', fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box', marginBottom:10 }}
                      />
                      {criandoErro && <div style={{ color:'#f87171', fontSize:12, marginBottom:8 }}>{criandoErro}</div>}
                      <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
                        <button onClick={() => { setCriandoLeadId(null); setNovoTexto(''); }} style={{ padding:'6px 14px', borderRadius:7, background:'transparent', color:'#64748b', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}>Cancelar</button>
                        <button onClick={criarNota} disabled={criando || !novoTexto.trim()} style={{ padding:'6px 16px', borderRadius:7, background: novoTexto.trim() ? 'rgba(246,201,14,0.15)' : '#1a2035', color: novoTexto.trim() ? '#f6c90e' : '#475569', border:`1px solid ${novoTexto.trim() ? 'rgba(246,201,14,0.4)' : '#1e2d45'}`, fontSize:13, fontWeight:700, cursor: novoTexto.trim() ? 'pointer' : 'not-allowed' }}>
                          {criando ? 'Salvando...' : 'Salvar Nota'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
